import React, { useState, useCallback } from 'react';
import { Search, Printer, RefreshCw, Trash2, X, Check, AlertCircle, Loader2, FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { printingService } from '../../../../services/printingService';
import { PrinterSelect } from '../../../common/PrinterSelect';
import type { PrintLog, PrintHistorySearchParams, PrintResult, CleanupResult } from '../../../../types/printing';

// ==================== REFERENCE TYPE LABELS ====================

type TFn = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

const buildReferenceTypeLabels = (t: TFn): Record<string, string> => ({
  Item: t('warehouse.modules.printHistory.referenceTypes.item', {}, 'Vareetikett'),
  Location: t('warehouse.modules.printHistory.referenceTypes.location', {}, 'Lokasjonsetikett'),
  Package: t('warehouse.modules.printHistory.referenceTypes.package', {}, 'Kolli-etikett'),
  SSCC: t('warehouse.modules.printHistory.referenceTypes.sscc', {}, 'SSCC/Pall-etikett'),
  PackingSlip: t('warehouse.modules.printHistory.referenceTypes.packingSlip', {}, 'Pakkseddel'),
  ShippingLabel: t('warehouse.modules.printHistory.referenceTypes.shippingLabel', {}, 'Fraktetikett'),
  TransportLabel: t('warehouse.modules.printHistory.referenceTypes.transportLabel', {}, 'TA-fraktetikett'),
  CargonizerLabel: t('warehouse.modules.printHistory.referenceTypes.transportLabel', {}, 'TA-fraktetikett'), // Legacy fallback for eksisterende data
  ConsignmentNote: t('warehouse.modules.printHistory.referenceTypes.consignmentNote', {}, 'Fraktbrev'),
  ExternalPackingSlip: t('warehouse.modules.printHistory.referenceTypes.externalPackingSlip', {}, 'Pakkseddel (ERP)'),
  External: t('warehouse.modules.printHistory.referenceTypes.external', {}, 'Ekstern utskrift'),
  Reprint: t('warehouse.modules.printHistory.reprint', {}, 'Reprint'),
});

const buildReferenceTypeOptions = (t: TFn) => [
  { value: '', label: t('warehouse.modules.printHistory.referenceTypes.all', {}, 'Alle typer') },
  { value: 'Item', label: t('warehouse.modules.printHistory.referenceTypes.item', {}, 'Vareetikett') },
  { value: 'Location', label: t('warehouse.modules.printHistory.referenceTypes.location', {}, 'Lokasjonsetikett') },
  { value: 'Package', label: t('warehouse.modules.printHistory.referenceTypes.package', {}, 'Kolli-etikett') },
  { value: 'SSCC', label: t('warehouse.modules.printHistory.referenceTypes.sscc', {}, 'SSCC/Pall-etikett') },
  { value: 'PackingSlip', label: t('warehouse.modules.printHistory.referenceTypes.packingSlip', {}, 'Pakkseddel') },
  { value: 'ShippingLabel', label: t('warehouse.modules.printHistory.referenceTypes.shippingLabel', {}, 'Fraktetikett') },
  { value: 'TransportLabel', label: t('warehouse.modules.printHistory.referenceTypes.transportLabel', {}, 'TA-fraktetikett') },
  { value: 'ConsignmentNote', label: t('warehouse.modules.printHistory.referenceTypes.consignmentNote', {}, 'Fraktbrev') },
  { value: 'External', label: t('warehouse.modules.printHistory.referenceTypes.external', {}, 'Ekstern utskrift') },
];

const buildStatusLabels = (t: TFn): Record<string, { text: string; className: string }> => ({
  Queued: { text: t('warehouse.modules.printHistory.statuses.queued', {}, 'I kø'), className: 'bg-yellow-100 text-yellow-800' },
  Success: { text: t('warehouse.modules.printHistory.statuses.success', {}, 'OK'), className: 'bg-green-100 text-green-800' },
  Failed: { text: t('warehouse.modules.printHistory.statuses.failed', {}, 'Feilet'), className: 'bg-red-100 text-red-800' },
});

// ==================== COMPONENT ====================

export const PrintHistory: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  const referenceTypeLabels = React.useMemo(() => buildReferenceTypeLabels(t), [t]);
  const referenceTypeOptions = React.useMemo(() => buildReferenceTypeOptions(t), [t]);
  const statusLabels = React.useMemo(() => buildStatusLabels(t), [t]);

  // Search state
  const [filters, setFilters] = useState<PrintHistorySearchParams>({
    referenceType: null,
    referenceId: null,
    operatorId: null,
    fromDate: null,
    toDate: null,
  });

  // Results state
  const [printLogs, setPrintLogs] = useState<PrintLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  // Reprint dialog state
  const [selectedLog, setSelectedLog] = useState<PrintLog | null>(null);
  const [reprintPrinterId, setReprintPrinterId] = useState<string | null>(null);
  const [reprintCopies, setReprintCopies] = useState(1);
  const [reprinting, setReprinting] = useState(false);
  const [reprintResult, setReprintResult] = useState<PrintResult | null>(null);

  // Download state
  const [downloadingLogId, setDownloadingLogId] = useState<number | null>(null);

  // Cleanup state
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);

  // ==================== SEARCH ====================

  const fetchPage = useCallback(async (page: number, size: number, searchFilters?: typeof filters) => {
    if (!companyNo) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    const activeFilters = searchFilters ?? filters;
    const offset = (page - 1) * size;

    try {
      const result = await printingService.searchPrintHistory(companyNo, {
        ...activeFilters,
        limit: size,
        offset,
      });
      setPrintLogs(result.items);
      setTotalCount(result.totalCount);
      setCurrentPage(page);
    } catch (err) {
      console.error('Search failed:', err);
      setError(err instanceof Error ? err.message : t('warehouse.modules.printHistory.errors.searchFailed', {}, 'Søk feilet'));
    } finally {
      setLoading(false);
    }
  }, [companyNo, filters, t]);

  const handleSearch = useCallback(async () => {
    setCurrentPage(1);
    await fetchPage(1, pageSize);
  }, [fetchPage, pageSize]);

  const handleLoadRecent = useCallback(async () => {
    const defaultFilters = {
      referenceType: null,
      referenceId: null,
      operatorId: null,
      fromDate: null,
      toDate: null,
      limit: pageSize,
    };
    setFilters(defaultFilters);
    setCurrentPage(1);
    await fetchPage(1, pageSize, defaultFilters);
  }, [fetchPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handlePageChange = useCallback((page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchPage(page, pageSize);
  }, [fetchPage, pageSize, totalPages, currentPage]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchPage(1, newSize);
  }, [fetchPage]);

  // ==================== REPRINT ====================

  const openReprintDialog = (log: PrintLog) => {
    setSelectedLog(log);
    setReprintPrinterId(null);
    setReprintCopies(log.numOfCopies || 1);
    setReprintResult(null);
  };

  const closeReprintDialog = () => {
    setSelectedLog(null);
    setReprintResult(null);
  };

  const handleReprint = async () => {
    if (!companyNo || !selectedLog || !reprintPrinterId) return;

    setReprinting(true);
    setReprintResult(null);

    try {
      const result = await printingService.reprint(companyNo, {
        originalLogId: selectedLog.logID,
        printerIdOverride: parseInt(reprintPrinterId, 10),
        numOfCopies: reprintCopies,
      });
      setReprintResult(result);

      // Refresh the list after successful reprint
      if (result.success) {
        setTimeout(() => {
          handleSearch();
        }, 1000);
      }
    } catch (err) {
      console.error('Reprint failed:', err);
      setReprintResult({
        success: false,
        message: err instanceof Error ? err.message : t('warehouse.modules.printHistory.errors.reprintFailed', {}, 'Reprint feilet'),
        labelsPrinted: 0,
      });
    } finally {
      setReprinting(false);
    }
  };

  // ==================== DOWNLOAD ====================

  const handleDownloadPdf = async (log: PrintLog) => {
    if (!companyNo) return;
    setDownloadingLogId(log.logID);
    setError(null);
    try {
      const blob = await printingService.downloadPrintLogPdf(companyNo, log.logID);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeRef = (log.referenceID || '').replace(/[^a-zA-Z0-9_-]+/g, '_');
      link.download = `print-${log.logID}-${log.referenceType || 'document'}-${safeRef}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError(err instanceof Error ? err.message : t('warehouse.modules.printHistory.errors.downloadFailed', {}, 'Nedlasting feilet'));
    } finally {
      setDownloadingLogId(null);
    }
  };

  // ==================== CLEANUP ====================

  const handleCleanup = async () => {
    if (!companyNo) return;

    setCleaningUp(true);
    setCleanupResult(null);
    setShowCleanupConfirm(false);

    try {
      const result = await printingService.cleanupPrintLogs(companyNo, 30);
      setCleanupResult(result);
      // Refresh list
      if (hasSearched) {
        handleSearch();
      }
    } catch (err) {
      console.error('Cleanup failed:', err);
      setError(err instanceof Error ? err.message : t('warehouse.modules.printHistory.errors.cleanupFailed', {}, 'Opprydding feilet'));
    } finally {
      setCleaningUp(false);
    }
  };

  // ==================== HELPERS ====================

  const formatDateTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nb-NO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('warehouse.modules.printHistory.header.title', {}, 'Utskriftshistorikk')}</h2>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              title={t('warehouse.modules.printHistory.header.perPageTitle', {}, 'Antall per side')}
            >
              <option value={50}>{t('warehouse.modules.printHistory.header.perPage', { '0': 50 }, '50 per side')}</option>
              <option value={100}>{t('warehouse.modules.printHistory.header.perPage', { '0': 100 }, '100 per side')}</option>
              <option value={200}>{t('warehouse.modules.printHistory.header.perPage', { '0': 200 }, '200 per side')}</option>
              <option value={500}>{t('warehouse.modules.printHistory.header.perPage', { '0': 500 }, '500 per side')}</option>
            </select>
            <button
              onClick={handleLoadRecent}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {t('warehouse.modules.printHistory.header.recent', {}, 'Siste')}
            </button>
            <button
              onClick={() => setShowCleanupConfirm(true)}
              disabled={cleaningUp}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors disabled:opacity-50"
              title={t('warehouse.modules.printHistory.header.cleanupTitle', {}, 'Slett utskriftslogger eldre enn 30 dager')}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('warehouse.modules.printHistory.header.cleanup', {}, 'Opprydding')}
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.fields.type', {}, 'Type')}</label>
            <select
              value={filters.referenceType || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, referenceType: e.target.value || null }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            >
              {referenceTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('warehouse.modules.printHistory.filters.referenceId', {}, 'Referanse-ID')}</label>
            <input
              type="text"
              value={filters.referenceId || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, referenceId: e.target.value || null }))}
              placeholder={t('warehouse.modules.printHistory.filters.referenceIdPlaceholder', {}, 'F.eks. varenr, lokasjon...')}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('warehouse.modules.printHistory.filters.fromDate', {}, 'Fra dato')}</label>
            <input
              type="date"
              value={filters.fromDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value || null }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('warehouse.modules.printHistory.filters.toDate', {}, 'Til dato')}</label>
            <input
              type="date"
              value={filters.toDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value || null }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <Search className="w-3.5 h-3.5" />
              {t('common.buttons.search', {}, 'Søk')}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Cleanup result */}
      {cleanupResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-700">
          <Check className="w-4 h-4 flex-shrink-0" />
          {t('warehouse.modules.printHistory.cleanup.resultSummary', { '0': cleanupResult.logsDeleted, '1': cleanupResult.filesDeleted }, 'Opprydding fullført: {0} logger slettet, {1} filer slettet')}
          {cleanupResult.fileDeleteErrors > 0 && t('warehouse.modules.printHistory.cleanup.resultErrors', { '0': cleanupResult.fileDeleteErrors }, ', {0} feil')}
        </div>
      )}

      {/* Results table */}
      {hasSearched && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              {t('warehouse.modules.printHistory.table.searching', {}, 'Søker...')}
            </div>
          ) : printLogs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('warehouse.modules.printHistory.table.empty', {}, 'Ingen utskriftshistorikk funnet')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('common.fields.date', {}, 'Dato')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('common.fields.type', {}, 'Type')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('warehouse.modules.printHistory.table.reference', {}, 'Referanse')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('warehouse.general.operator', {}, 'Operatør')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('warehouse.modules.printHistory.table.printer', {}, 'Skriver')}</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">{t('common.fields.quantity', {}, 'Antall')}</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">{t('common.fields.status', {}, 'Status')}</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">{t('warehouse.modules.printHistory.table.action', {}, 'Handling')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {printLogs.map((log) => {
                    const status = statusLabels[log.status] || { text: log.status, className: 'bg-gray-100 text-gray-800' };
                    const canReprint = log.pdfFilePath != null && log.status !== 'Queued';
                    const canDownload = log.pdfFilePath != null;
                    const isDownloading = downloadingLogId === log.logID;

                    return (
                      <tr key={log.logID} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                          {formatDateTime(log.printedDateTime)}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {referenceTypeLabels[log.referenceType || ''] || log.referenceType || '-'}
                          {log.originalLogID && (
                            <span className="ml-1 text-xs text-blue-600">(#{log.originalLogID})</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700 font-mono text-xs">
                          {log.referenceID || '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {log.operatorID || '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-700 text-xs max-w-[150px] truncate" title={log.printerName || ''}>
                          {log.printerName || '-'}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-700">
                          {log.numOfCopies}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                            {status.text}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {canReprint && (
                              <button
                                onClick={() => openReprintDialog(log)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded transition-colors"
                                title={t('warehouse.modules.printHistory.reprintTitle', {}, 'Skriv ut på nytt')}
                              >
                                <Printer className="w-3 h-3" />
                                {t('warehouse.modules.printHistory.reprint', {}, 'Reprint')}
                              </button>
                            )}
                            {canDownload && (
                              <button
                                onClick={() => handleDownloadPdf(log)}
                                disabled={isDownloading}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 rounded transition-colors disabled:opacity-50"
                                title={t('warehouse.modules.printHistory.downloadPdfTitle', {}, 'Last ned PDF')}
                              >
                                {isDownloading ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Download className="w-3 h-3" />
                                )}
                                PDF
                              </button>
                            )}
                            {!canReprint && !canDownload && (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {printLogs.length > 0 && (
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {totalCount === 1
                  ? t('warehouse.modules.printHistory.pagination.showingOne', { '0': ((currentPage - 1) * pageSize) + 1, '1': Math.min(currentPage * pageSize, totalCount), '2': totalCount }, 'Viser {0}–{1} av {2} resultat')
                  : t('warehouse.modules.printHistory.pagination.showingMany', { '0': ((currentPage - 1) * pageSize) + 1, '1': Math.min(currentPage * pageSize, totalCount), '2': totalCount }, 'Viser {0}–{1} av {2} resultater')}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('warehouse.modules.printHistory.pagination.firstPage', {}, 'Første side')}
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('warehouse.modules.printHistory.pagination.previousPage', {}, 'Forrige side')}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2 text-xs text-gray-600 font-medium">
                    {t('warehouse.modules.printHistory.pagination.pageOf', { '0': currentPage, '1': totalPages }, 'Side {0} av {1}')}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('warehouse.modules.printHistory.pagination.nextPage', {}, 'Neste side')}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('warehouse.modules.printHistory.pagination.lastPage', {}, 'Siste side')}
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== REPRINT DIALOG ==================== */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Printer className="w-5 h-5 text-blue-600" />
                {t('warehouse.modules.printHistory.reprintTitle', {}, 'Skriv ut på nytt')}
              </h3>
              <button
                onClick={closeReprintDialog}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Original info */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('warehouse.modules.printHistory.reprintDialog.originalPrint', {}, 'Original utskrift:')}</span>
                  <span className="font-medium">#{selectedLog.logID}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('warehouse.modules.printHistory.reprintDialog.typeLabel', {}, 'Type:')}</span>
                  <span>{referenceTypeLabels[selectedLog.referenceType || ''] || selectedLog.referenceType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('warehouse.modules.printHistory.reprintDialog.referenceLabel', {}, 'Referanse:')}</span>
                  <span className="font-mono">{selectedLog.referenceID}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('warehouse.modules.printHistory.reprintDialog.dateLabel', {}, 'Dato:')}</span>
                  <span>{formatDateTime(selectedLog.printedDateTime)}</span>
                </div>
              </div>

              {/* Printer selection */}
              <PrinterSelect
                value={reprintPrinterId}
                onChange={setReprintPrinterId}
                label={t('warehouse.modules.printHistory.reprintDialog.selectPrinter', {}, 'Velg skriver')}
                size="md"
                showTestButton={true}
              />

              {/* Copies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.printHistory.reprintDialog.copies', {}, 'Antall kopier')}</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setReprintCopies(Math.max(1, reprintCopies - 1))}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    disabled={reprintCopies <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={reprintCopies}
                    onChange={(e) => setReprintCopies(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                    className="w-16 text-center px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    min={1}
                    max={100}
                  />
                  <button
                    onClick={() => setReprintCopies(Math.min(100, reprintCopies + 1))}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    disabled={reprintCopies >= 100}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Result feedback */}
              {reprintResult && (
                <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${
                  reprintResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {reprintResult.success ? (
                    <Check className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  {reprintResult.message}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={closeReprintDialog}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleReprint}
                disabled={reprinting || !reprintPrinterId}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reprinting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                {t('common.buttons.print', {}, 'Skriv ut')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CLEANUP CONFIRMATION DIALOG ==================== */}
      {showCleanupConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-4">
            <h3 className="font-semibold text-gray-900 mb-2">{t('warehouse.modules.printHistory.cleanupDialog.title', {}, 'Bekreft opprydding')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('warehouse.modules.printHistory.cleanupDialog.message', {}, 'Dette vil slette alle utskriftslogger og arkiverte PDF-filer som er eldre enn 30 dager. Denne handlingen kan ikke angres.')}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCleanupConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleCleanup}
                disabled={cleaningUp}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {cleaningUp ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {t('warehouse.modules.printHistory.cleanupDialog.confirm', {}, 'Slett gamle data')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintHistory;
